import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import StepTitle from '../atoms/titles/StepTitle';
import GroupTitle from '../atoms/titles/GroupTitle';
import SearchBoxDateAirport from '../organisms/SearchBoxDateAirport';
import SearchBoxFacility from '../organisms/SearchBoxFacility';
import PlanePlan from '../organisms/PlanePlan';
import SortList from '../molecules/SortList';
import DataUpdateTimeDisplay from '../molecules/DataUpdateTimeDisplay';
import SearchResultFacility from '../organisms/SearchResultFacility';
import SearchBoxSP from '../organisms/SearchBoxSP';
import { getFilghtInputValidationErrors } from '../../modules/flight';
import {
  fetchHotels,
  setSortOrder,
} from '../../modules/hotel';
import { fetchPlans } from '../../modules/plan';
import { useSearchFlightSync } from '../../modules/hooks/searchFlight';
import { useProgress } from '../../modules/hooks/progress';
import { makeFlightsInput, makeHotelsInput, makePlansInput } from '../../utils/input';
import { useWindowSize } from '../../utils/windowSize';
import Loading from '../atoms/Loading';
import PlanList from './PlanList';
import { SHOWING_HOTEL_LIMIT } from '../../constants';

const Search = ({ affiliate }) => {
  const [isPCView, setIsPCView] = useState(false);
  const windowSize = useWindowSize();
  useEffect(() => {
    const pcView = windowSize.width > 768;
    setIsPCView(pcView);
  }, [windowSize]);

  const {
    dispatch, flight, hotel, plan, handleFlightSearch,
  } = useSearchFlightSync(true, false, false, true);

  const {
    fetchingOutbound,
    fetchingInbound,
    flightsInput,
    queryParamsValidated: queryParamsValidatedFlight,
    outboundFlights,
    inboundFlights,
    calenderMinDateOutbound,
  } = flight;
  const {
    fetching: fetchingHotel,
    hotelsInput,
    queryParamsValidated: queryParamsValidatedHotel,
    paginatorInfo,
    hotels,
    group,
  } = hotel;
  const {
    hotelId,
  } = plan;

  useEffect(() => {
    if (!queryParamsValidatedFlight || !queryParamsValidatedHotel) {
      return;
    }

    const { people, airline, outbound } = flightsInput;
    const outboundFlightsInput = makeFlightsInput(people, airline, outbound, outbound.date, true);
    const errorMessages = getFilghtInputValidationErrors(outboundFlightsInput);
    if (errorMessages && errorMessages.length) {
      return;
    }

    // affiliateの時もsearchedHotelInputの保存のために（不要かも？）
    dispatch(
      fetchHotels(
        makeHotelsInput(hotelsInput, flightsInput),
        SHOWING_HOTEL_LIMIT,
        1
      )
    );
  }, [queryParamsValidatedFlight, queryParamsValidatedHotel]);

  const handleHotelSearch = useCallback(() => {
    if (isPCView) {
      const validated = handleFlightSearch();
      if (!validated) {
        return;
      }
    }

    dispatch(
      fetchHotels(
        makeHotelsInput(hotelsInput, flightsInput),
        SHOWING_HOTEL_LIMIT,
        1
      )
    );
  }, [hotelsInput, flightsInput, handleFlightSearch]);

  const handlePlansSearchAffiliate = useCallback(() => {
    if (isPCView) {
      const validated = handleFlightSearch();
      if (!validated) {
        return;
      }
    }

    const totalPlanPerPage = 10;

    dispatch(
      fetchPlans(
        makePlansInput(hotelsInput, flightsInput, hotelId),
        hotelsInput,
        totalPlanPerPage,
        1
      )
    );
  }, [hotelsInput, flightsInput, hotelId, handleFlightSearch]);

  const handleHotelSearchWithCallback = useCallback((callback) => {
    const validated = handleFlightSearch();
    if (!validated) {
      return;
    }

    dispatch(
      fetchHotels(
        makeHotelsInput(hotelsInput, flightsInput),
        SHOWING_HOTEL_LIMIT,
        1,
        callback
      )
    );
  }, [hotelsInput, flightsInput, handleFlightSearch]);

  const { fetching, progressRate } = useProgress(fetchingOutbound, fetchingInbound, fetchingHotel);

  // hotel再検索時にflightsも一緒に検索するならflightsInputからmaxStaynightsを決めて問題ない
  const tripStartDate = moment(flightsInput ? flightsInput.outbound.date : undefined);
  const tripEndDate = moment(flightsInput ? flightsInput.inbound.date : undefined);
  const maxStayNights = queryParamsValidatedFlight ? tripEndDate.diff(tripStartDate, 'days') : undefined;

  const validOutbound = !!(outboundFlights && outboundFlights.length);
  const validInbound = !!(inboundFlights && inboundFlights.length);
  const showSortList = !!(validOutbound && validInbound && hotels && hotels.length);

  const dataUpdateTime = !!(hotels && hotels.length) && hotels[0].dataUpdateTime;

  return (
    <React.Fragment>
      <div id="rt_air_emergency">{/* rt_air_emergency */}</div>
      <div className="pageSearch pagePlanListAffiliate">
        <Loading show={fetching} progressRate={progressRate} />
        {group && (
          <GroupTitle text={group} />
        )}
        <div className="isPC">
          <StepTitle text="STEP① 日程・航空便選択" />
          <div className="searchBoxDateAirportWrapper">
            <SearchBoxDateAirport
              handleSearch={!affiliate ? handleHotelSearch : handlePlansSearchAffiliate}
              calenderMinDateOutbound={calenderMinDateOutbound}
            />
          </div>
          <div className="planePlanWrapper">
            <PlanePlan handleFlightSearch={handleFlightSearch} />
          </div>
          {!affiliate ? (
            <React.Fragment>
              <div className="step2Wrapper">
                <StepTitle text="STEP② 宿泊施設選択" />
              </div>
              <div className="searchBoxFacilityWrapper">
                <SearchBoxFacility
                  handleSearch={handleHotelSearch}
                  limitedOptions={affiliate}
                  maxStayNights={maxStayNights}
                />
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className="stepTitleAffiliateWrapper">
                <StepTitle text="STEP② プラン選択" className="stepTitleAffiliate" />
              </div>
            </React.Fragment>
          )}
        </div>
        <div className="isSP" style={{ marginBottom: affiliate ? 20 : undefined }}>
          <SearchBoxSP
            affiliate={affiliate}
            handleFlightSearch={handleFlightSearch}
            handleSearchWithCallback={handleHotelSearchWithCallback}
            maxStayNights={maxStayNights}
          />
        </div>
        {!affiliate ? (
          <React.Fragment>
            {showSortList && (
              <div className="sortListWrapper">
                <SortList
                  value={hotelsInput.sortOrder}
                  handleChange={(sortOrder) => {
                    dispatch(setSortOrder(sortOrder));

                    if (!paginatorInfo) {
                      return;
                    }

                    const { currentPage } = paginatorInfo;
                    const newhotelsInput = {
                      ...hotelsInput,
                      sortOrder,
                    };

                    dispatch(
                      fetchHotels(
                        makeHotelsInput(newhotelsInput, flightsInput),
                        SHOWING_HOTEL_LIMIT,
                        currentPage
                      )
                    );
                  }}
                />
                {!!dataUpdateTime && (
                  <DataUpdateTimeDisplay dataUpdateTime={dataUpdateTime} />
                )}
              </div>
            )}
            <div className="searchResultFacilityWrapper">
              <SearchResultFacility hidePlanListLink={false} fetchHotelsInput={makeHotelsInput(hotelsInput, flightsInput)} />
            </div>
          </React.Fragment>
        ) : (
          <PlanList onSearchPlans={handleFlightSearch} affiliate={affiliate} />
        )}
      </div>
    </React.Fragment>
  );
};

Search.propTypes = {
  affiliate: PropTypes.bool,
};

export default Search;
